import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  SparklesIcon, 
  CheckCircleIcon, 
  ExclamationTriangleIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import { Button } from '@/components/ui/Button';
import { LiquidGlassCard } from '@/components/liquid-glass/LiquidGlassCard';
import { useMigration } from '@/hooks/useMigration';
import { cn } from '@/lib/utils';

interface MigrationTriggerProps {
  className?: string;
  onComplete?: () => void;
}

export function MigrationTrigger({ className, onComplete }: MigrationTriggerProps) {
  const { isRunning, error, result, runConversationEnhancement, clearResult } = useMigration();
  const [showDetails, setShowDetails] = useState(false);

  const handleRunMigration = async (forceReprocess = false) => {
    try {
      await runConversationEnhancement(20, forceReprocess);
      if (onComplete) {
        onComplete();
      }
    } catch (err) {
      // Error is already handled by the hook
      console.error('Migration trigger error:', err);
    }
  };

  if (result && result.success) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className={cn('w-full', className)}
      >
        <LiquidGlassCard variant="active" className="p-4">
          <div className="text-center">
            <CheckCircleIcon className="h-12 w-12 text-green-400 mx-auto mb-3" />
            <h3 className="text-sm font-medium text-foreground mb-2">
              Migration Complete!
            </h3>
            <div className="text-xs text-muted-foreground space-y-1">
              <div>Enhanced: {result.progress?.enhancedConversations || result.enhancedConversations || 0} conversations</div>
              <div>Total: {result.progress?.processedConversations || result.totalConversations || 0} processed</div>
              <div>Message: {result.message}</div>
            </div>
            
            <div className="flex gap-2 mt-4">
              <Button
                onClick={clearResult}
                variant="ghost"
                size="sm"
                className="text-xs"
              >
                Close
              </Button>
              <Button
                onClick={() => setShowDetails(!showDetails)}
                variant="ghost"
                size="sm"
                className="text-xs"
              >
                {showDetails ? 'Hide' : 'Show'} Details
              </Button>
            </div>
            
            <AnimatePresence>
              {showDetails && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mt-3 p-3 bg-white/5 rounded-lg text-left"
                >
                  <div className="text-xs text-muted-foreground">
                    <div className="font-medium mb-2">Migration Results:</div>
                    <div>• Total conversations found: {result.progress?.totalConversations || result.totalConversations || 0}</div>
                    <div>• Successfully enhanced: {result.progress?.enhancedConversations || result.enhancedConversations || 0}</div>
                    <div>• Processed: {result.progress?.processedConversations || 0}</div>
                    <div>• Errors encountered: {result.progress?.errors || result.errors || 0}</div>
                    <div>• Message: {result.message}</div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </LiquidGlassCard>
      </motion.div>
    );
  }

  if (error) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className={cn('w-full', className)}
      >
        <LiquidGlassCard variant="active" className="p-4">
          <div className="text-center">
            <ExclamationTriangleIcon className="h-12 w-12 text-red-400 mx-auto mb-3" />
            <h3 className="text-sm font-medium text-foreground mb-2">
              Migration Failed
            </h3>
            <div className="text-xs text-red-400 mb-4">
              {error}
            </div>
            
            <div className="flex gap-2">
              <Button
                onClick={clearResult}
                variant="ghost"
                size="sm"
                className="text-xs"
              >
                Close
              </Button>
              <Button
                onClick={handleRunMigration}
                variant="ghost"
                size="sm"
                className="text-xs text-primary"
                disabled={isRunning}
              >
                Try Again
              </Button>
            </div>
          </div>
        </LiquidGlassCard>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      className={cn('w-full', className)}
    >
      <LiquidGlassCard variant="default" className="p-4">
        <div className="text-center">
          <SparklesIcon className="h-12 w-12 text-primary mx-auto mb-3" />
          <h3 className="text-sm font-medium text-foreground mb-2">
            Enhance Your Conversations
          </h3>
          <p className="text-xs text-muted-foreground mb-4">
            Upgrade your existing conversations with AI-powered categorization, insights, and advanced features.
          </p>
          
          <div className="bg-white/5 rounded-lg p-3 text-xs text-left mb-4">
            <div className="font-medium text-foreground mb-2">What you'll get:</div>
            <div className="space-y-1 text-muted-foreground">
              <div>• Automatic conversation categorization</div>
              <div>• AI-generated summaries and insights</div>
              <div>• Action item tracking</div>
              <div>• Advanced search and filtering</div>
              <div>• Performance analytics</div>
            </div>
          </div>
          
          <Button
            onClick={handleRunMigration}
            disabled={isRunning}
            className={cn(
              'w-full text-xs',
              isRunning && 'cursor-not-allowed opacity-50'
            )}
          >
            {isRunning ? (
              <>
                <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
                Enhancing Conversations...
              </>
            ) : (
              <>
                <SparklesIcon className="h-4 w-4 mr-2" />
                Start Enhancement
              </>
            )}
          </Button>
          
          {!isRunning && (
            <>
              <Button
                onClick={() => handleRunMigration(true)}
                variant="outline"
                className="w-full text-xs mt-2"
                disabled={isRunning}
              >
                Force Reprocess All Conversations
              </Button>
              <div className="text-xs text-muted-foreground mt-2">
                Use this to re-analyze conversations with improved AI
              </div>
            </>
          )}
          
          {isRunning && (
            <div className="mt-3 text-xs text-muted-foreground">
              This may take a few moments...
            </div>
          )}
        </div>
      </LiquidGlassCard>
    </motion.div>
  );
}